import React from 'react';
import type { LodgementInput, LodgementStatus } from '../../hooks/useLodgements';
import { OwnerSelect, type OwnerOptionGroup } from './OwnerSelect';
import {
  Field, inputCls, STATUS_OPTIONS, MIR_CATEGORIES, STRUCTURES, money, fmtDate, EMPTY,
} from './LodgementDisplay';

/**
 * The lodgement entry form, used for BOTH create and edit.
 *
 * ONE FORM, NOT TWO. An edit screen that drifts from the entry screen is how a
 * field ends up enterable but not correctable, which is the state the register
 * was in: rows could be captured and then never fixed. The only differences
 * between the modes are the title, the button label and what the owner picker
 * means, all of which are props rather than a forked component.
 *
 * SETTLEMENT IS NEVER A FIELD. `settledDate` / `settledAmountCents` are absent
 * from `LodgementInput` by construction, so this form cannot forge or clobber a
 * settlement even in edit mode. On an already-reconciled row they are shown
 * read-only, so the user can see the figure they are not allowed to type.
 */

export interface SettlementReadonly {
  settledDate: string | null;
  settledAmountCents: number | null;
  settlementSource: string | null;
}

interface Props {
  mode: 'create' | 'edit';
  form: LodgementInput;
  onChange: <K extends keyof LodgementInput>(k: K, v: LodgementInput[K]) => void;
  canPickOwner: boolean;
  ownerGroups: OwnerOptionGroup[];
  lenders: string[];
  lenderSource: string;
  /** Present only in edit mode, and only once the reconciler has matched. */
  settlement?: SettlementReadonly | null;
  saving: boolean;
  saveErr: string | null;
  onCancel: () => void;
  onSave: () => void;
}

/**
 * Mirrors the server derivation so the user sees it before saving.
 *
 * Time-to-formal only. There is deliberately no "week" here: the source
 * spreadsheet's week label matched no day-of-month rule (54% vs 46% across
 * 1520 rows) because it was a hand-typed section header. Period grouping is a
 * read-time concern, so nothing about it is entered or previewed.
 */
export function derivedPreview(f: LodgementInput): { days: number | null } {
  let days: number | null = null;
  if (f.dateApproved) {
    const a = Date.parse(`${f.dateLodged}T00:00:00Z`);
    const b = Date.parse(`${f.dateApproved}T00:00:00Z`);
    if (!Number.isNaN(a) && !Number.isNaN(b)) days = Math.round((b - a) / 86_400_000);
  }
  return { days };
}

export function LodgementFormModal({
  mode, form, onChange, canPickOwner, ownerGroups, lenders, lenderSource,
  settlement, saving, saveErr, onCancel, onSave,
}: Props): React.ReactElement {
  const preview = derivedPreview(form);
  const isEdit = mode === 'edit';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="max-h-[88vh] w-full max-w-3xl overflow-auto rounded-xl border border-gray-700 bg-gray-900">
        <div className="sticky top-0 flex items-start justify-between border-b border-gray-700 bg-gray-900 px-5 py-4">
          <div>
            <h3 className="text-lg font-bold text-white">
              {isEdit ? 'Edit lodgement' : 'New lodgement'}
            </h3>
            <p className="text-[11px] text-gray-400">
              {preview.days !== null
                ? `${preview.days} days to formal, computed rather than entered`
                : 'Time to formal is computed once approved'}
            </p>
          </div>
          <button onClick={onCancel} className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-white">✕</button>
        </div>

        <div className="grid gap-3 px-5 py-4 sm:grid-cols-2">
          {/*
            Full name, not a surname. The reconciler keys on the whole name
            (order-insensitive but token-exact), so "Smith" cannot match a
            commission row for "John Smith" and the settlement would never
            auto-fill. The hint says why, because a broker who does not know
            that will reasonably type the short form.
          */}
          <Field
            label="Client's name"
            required
            className="sm:col-span-2"
            hint="full name as it appears on the loan; settlements are matched on this"
          >
            <input value={form.clientName} onChange={(e) => onChange('clientName', e.target.value)} className={inputCls} placeholder="e.g. John Smith, or John and Mary Smith" />
          </Field>

          {/*
            Owner. Only for a role the callable lets write an arbitrary owner;
            everyone else's rows are attributed to them server-side, so a picker
            would be a control that silently does nothing.

            The empty option means different things by mode, which is why the
            label differs. On CREATE it omits the key entirely, which is what
            the server reads as own-work. On EDIT it means "leave the stored
            owner alone": an edit is not a reattribution, and the page only puts
            an ownerId on the wire when this picker was actively moved.
          */}
          {canPickOwner && (
            <Field
              label="Owner"
              className="sm:col-span-2"
              hint={isEdit
                ? 'change this only to reattribute the lodgement to someone else'
                : 'whose lodgement this is; leave as Myself for your own work'}
            >
              <OwnerSelect
                value={form.ownerId ?? ''}
                onChange={(ref) => onChange('ownerId', ref || undefined)}
                groups={ownerGroups}
                emptyLabel={isEdit ? 'Leave owner unchanged' : 'Myself'}
                className={inputCls}
              />
            </Field>
          )}

          <Field label="Date lodged" required hint="all period grouping derives from this">
            <input type="date" value={form.dateLodged} onChange={(e) => onChange('dateLodged', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Date approved" hint="time to formal is computed from this">
            <input type="date" value={form.dateApproved ?? ''} onChange={(e) => onChange('dateApproved', e.target.value || null)} className={inputCls} />
          </Field>

          <Field
            label="Lender"
            required
            hint={lenderSource === 'tenant'
              ? 'suggestions from your settled loans; any lender can be typed'
              : 'type any lender'}
          >
            <input value={form.lender} onChange={(e) => onChange('lender', e.target.value)} className={inputCls} list="lender-list" placeholder="Start typing" />
            <datalist id="lender-list">{lenders.map((l) => <option key={l} value={l} />)}</datalist>
          </Field>
          <Field label="Lodge loan amount" required hint="dollars; stored as integer cents">
            <input
              type="number" min={0} step={1}
              value={form.lodgeAmountCents ? form.lodgeAmountCents / 100 : ''}
              onChange={(e) => onChange('lodgeAmountCents', Math.round(Number(e.target.value || 0) * 100))}
              className={inputCls} placeholder="650000"
            />
          </Field>

          <Field label="Loan status" required hint="mirrors MyCRM SystemStatus">
            <select value={form.status} onChange={(e) => onChange('status', e.target.value as LodgementStatus)} className={inputCls}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Structure">
            <select value={form.structure ?? ''} onChange={(e) => onChange('structure', e.target.value || null)} className={inputCls}>
              <option value="">{EMPTY}</option>
              {STRUCTURES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>

          <Field label="MIR raised" hint="More Information Requested">
            <select
              value={form.mirs.length ? 'yes' : 'no'}
              onChange={(e) => onChange('mirs', e.target.value === 'yes' ? [{ category: 'Docs/Income', reason: '' }] : [])}
              className={inputCls}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Field>
          <Field label="MIR category">
            <select
              disabled={form.mirs.length === 0}
              value={form.mirs[0]?.category ?? ''}
              onChange={(e) => onChange('mirs', [{ category: e.target.value, reason: form.mirs[0]?.reason ?? '' }])}
              className={inputCls}
            >
              {MIR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          {form.mirs.length > 0 && (
            <Field label="MIR reason" className="sm:col-span-2">
              <input
                value={form.mirs[0]?.reason ?? ''}
                onChange={(e) => onChange('mirs', [{ category: form.mirs[0]?.category ?? 'Docs/Income', reason: e.target.value }])}
                className={inputCls} placeholder="What did the lender ask for?"
              />
            </Field>
          )}

          <Field label="One touch">
            <select value={form.oneTouch ? 'yes' : 'no'} onChange={(e) => onChange('oneTouch', e.target.value === 'yes')} className={inputCls}>
              <option value="no">No</option><option value="yes">Yes</option>
            </select>
          </Field>
          <Field label="Google review asked">
            <select value={form.googleReviewAsked ? 'yes' : 'no'} onChange={(e) => onChange('googleReviewAsked', e.target.value === 'yes')} className={inputCls}>
              <option value="no">No</option><option value="yes">Yes</option>
            </select>
          </Field>

          <Field label="Remarks" className="sm:col-span-2">
            <textarea rows={2} value={form.remarks ?? ''} onChange={(e) => onChange('remarks', e.target.value || null)} className={inputCls} />
          </Field>

          {settlement?.settledDate ? (
            <div className="rounded-lg border border-emerald-700/40 bg-emerald-900/10 p-3 text-[11px] text-emerald-200 sm:col-span-2">
              Settled {fmtDate(settlement.settledDate)} for {money(settlement.settledAmountCents)}
              {settlement.settlementSource ? ` (${settlement.settlementSource.replace(/_/g, ' ')})` : ''}.
              These figures come from the commission pipeline and are not editable here.
            </div>
          ) : (
            <div className="rounded-lg border border-blue-700/40 bg-blue-900/10 p-3 text-[11px] text-blue-200 sm:col-span-2">
              Settlement date and settled amount are not entered here. They
              arrive from the commission pipeline and fill in automatically.
            </div>
          )}

          {saveErr && (
            <div className="rounded-lg border border-red-700/40 bg-red-900/10 p-3 text-[12px] text-red-200 sm:col-span-2">
              {saveErr}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-gray-700 bg-gray-900 px-5 py-4">
          <button onClick={onCancel} className="rounded-md border border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800">Cancel</button>
          <button
            onClick={onSave}
            disabled={saving || !form.clientName.trim() || !form.lender.trim() || !form.lodgeAmountCents}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save lodgement'}
          </button>
        </div>
      </div>
    </div>
  );
}
